/* eslint-disable react/forbid-prop-types */
// prop-type is a library for typechecking of props
/*
import { ErrorMessage } from "formik";
*/

import { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { ErrorMessage } from "formik";

import {
  InputLabel,
  Switch,
  Grid,
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import MDBox from "components/MDBox";
import FormField from "components/FormField";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

function Form({ formData, payGroups: payGroupsProp }) {
  const { formField, values, errors, touched, setFieldValue } = formData;
  const { firstName, lastName, username, email, plainPassword, payGroups, role, status } =
    formField;
  const {
    firstName: firstNameV,
    lastName: lastNameV,
    username: usernameV,
    email: emailV,
    plainPassword: plainPasswordV,
    payGroups: payGroupsV,
    role: roleV,
    status: statusV,
  } = values;

  const normalizedPayGroup = useMemo(
    () => payGroupsProp.reduce((all, one) => ({ ...all, [one.id]: one }), {}),
    [payGroupsProp]
  );

  const payGroupsValueOptionFormat = useMemo(
    () =>
      payGroupsV
        .filter((payGroupsId) => normalizedPayGroup[payGroupsId])
        .map((payGroupsId) => ({
          label: normalizedPayGroup[payGroupsId].name,
          id: normalizedPayGroup[payGroupsId].id,
        })),
    [normalizedPayGroup, payGroupsV]
  );

  const payGroupOptions = useMemo(
    () =>
      payGroupsProp.map((l) => ({
        label: l.name,
        id: l.id,
      })),
    [payGroupsProp]
  );
  const statusValue = useMemo(() => statusV === "active", [statusV]);

  const onStatusChange = useCallback(() => {
    const nv = statusValue ? "inactive" : "active";
    setFieldValue(status.name, nv);
  }, [statusValue, setFieldValue]);

  useEffect(() => {
    switch (statusV) {
      case 1:
      case "1": {
        setFieldValue(status.name, "active");
        break;
      }
      case 0:
      case "0": {
        setFieldValue(status.name, "inactive");
        break;
      }
      default: {
        break;
      }
    }
  }, [status.name, statusV, setFieldValue]);

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormField
            type={firstName.type}
            label={firstName.label}
            name={firstName.name}
            value={firstNameV}
            placeholder={firstName.placeholder}
            error={errors.firstName && touched.firstName}
            success={firstNameV.length > 0 && !errors.firstName}
          />
        </Grid>

        <Grid item xs={12}>
          <FormField
            type={lastName.type}
            label={lastName.label}
            name={lastName.name}
            value={lastNameV}
            placeholder={lastName.placeholder}
            error={errors.lastName && touched.lastName}
            success={lastNameV.length > 0 && !errors.lastName}
          />
        </Grid>

        <Grid item xs={12}>
          <FormField
            type={username.type}
            label={username.label}
            name={username.name}
            value={usernameV}
            placeholder={username.placeholder}
            error={errors.username && touched.username}
            success={usernameV.length > 0 && !errors.username}
          />
        </Grid>

        <Grid item xs={12}>
          <FormField
            type={email.type}
            label={email.label}
            name={email.name}
            value={emailV}
            placeholder={email.placeholder}
            error={errors.email && touched.email}
            success={emailV.length > 0 && !errors.email}
          />
        </Grid>

        <Grid item xs={12}>
          <FormField
            type={plainPassword.type}
            label={plainPassword.label}
            name={plainPassword.name}
            value={plainPasswordV}
            placeholder={plainPassword.placeholder}
            error={errors.plainPassword && touched.plainPassword}
            success={plainPasswordV?.length > 0 && !errors.plainPassword}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            multiple
            value={payGroupsValueOptionFormat}
            options={payGroupOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) =>
              setFieldValue(
                payGroups.name,
                value?.map((vOpt) => vOpt.id, true)
              )
            }
            isOptionEqualToValue={(option, value) => {
              switch (typeof value) {
                case "object":
                  return option?.id === value.id;
                case "string":
                  return option?.id === value;
                default:
                  return option === value;
              }
            }}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={payGroups.label}
                name={payGroups.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.payGroups && touched.payGroups}
                // success={payGroupsV && payGroupsV.length > 0 && !errors.payGroups}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={payGroups.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <MDBox mb={1.5}>
            <FormControl fullWidth>
              <InputLabel id="role-select-label">{role.label}</InputLabel>

              <Select
                labelId="role-select-label"
                id="role-select"
                value={roleV}
                label={role.label}
                onChange={(e) => setFieldValue(role.name, e.target.value)}
                style={{ height: 53 }}
              >
                <MenuItem value="ROLE_ADMIN">Admin</MenuItem>
                <MenuItem value="ROLE_USER">User</MenuItem>
              </Select>
            </FormControl>

            <MDBox mt={0.75}>
              <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
                <ErrorMessage name={role.name} />
              </MDTypography>
            </MDBox>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <InputLabel>{status.label}</InputLabel>
          <Switch checked={statusValue} onChange={onStatusChange} />
        </Grid>
      </Grid>
    </MDBox>
  );
}

// typechecking props for Form
Form.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  payGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Form;
